import React, { ChangeEvent, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, styled, TextField, TextFieldProps } from '@mui/material';
import Stack from '@mui/material/Stack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { localeFormatterHelper } from '../../../helpers/formatter/localeFormatter.helper.ts';
import { companyConfigService } from '../../../services/companyConfig/companyConfig.service.ts';

export const NumericField = forwardRef<HTMLInputElement, TNumericFieldProps>(
  (
    {
      shrink,
      controls = false,
      step = 1,
      min = 0,
      max = 10000000,
      onChange,
      onBlur,
      defaultValue,
      precision,
      noTrailingZeros = false,
      value,
      width,
      onKeyDown,
      returnZero = false,
      slotProps,
      disableDecrease = false,
      keyChange = true,
      formattingToLocal = true,
      ...props
    },
    ref,
  ) => {
    const initVal =
      (typeof value === 'number' ? (returnZero ? String(value) : value || null) : null) ||
      (typeof defaultValue === 'number'
        ? returnZero
          ? defaultValue
          : defaultValue || null
        : null);

    const [focused, setFocused] = useState(false);
    const cachedPrecision = companyConfigService.getCachedConfigs()?.decimalPlacesCount || 2;
    const effectivePrecision = precision !== undefined ? precision : cachedPrecision;

    const format = useCallback(
      (numV: string | number | null) => {
        if (returnZero && numV === null) return '';
        if (formattingToLocal) {
          return localeFormatterHelper.formatNumber(Number(numV!), {
            precision: effectivePrecision,
            returnZero,
            noTrailingZeros,
          });
        } else return String(Number(numV) || '');
      },
      [effectivePrecision, noTrailingZeros, returnZero],
    );

    const fixToPrecision = useCallback(
      (numV: number): number => {
        return Number(numV.toFixed(effectivePrecision));
      },
      [effectivePrecision],
    );

    /**
     * The same value as `format`, but as an *editable* string: no locale
     * thousands separator.
     *
     * `format` produces what a number should look like when read ("1'500");
     * this produces what it has to look like when it is about to be typed into
     * ("1500"). Deriving it from the number rather than by stripping characters
     * out of the formatted string is what makes it locale-safe: in `de` the
     * group separator IS `.`, so no amount of string surgery can tell
     * "1.500" (one thousand five hundred) from "1.500" (one and a half)
     * after the fact (WA-3803).
     */
    const formatRaw = useCallback(
      (numV: number | null): string => {
        if (numV === null) return '';
        // A `returnZero` field shows a formatted zero ("0.00"); as editable
        // text that is just "0", so the first digit typed replaces it.
        if (returnZero && numV === 0) return '0';
        if (!formattingToLocal) return String(Number(numV) || '');
        return localeFormatterHelper.formatNumber(numV, {
          precision: effectivePrecision,
          returnZero,
          noTrailingZeros,
          useGrouping: false,
        });
      },
      [effectivePrecision, noTrailingZeros, returnZero, formattingToLocal],
    );

    /* Focus tracked in a ref as well as in state: the resync effect below has
    to read it, and with `autoFocus` the focus event fires from a layout effect,
    i.e. before that passive effect runs, so the state update is not visible to
    it yet (WA-3803). */
    const focusedRef = useRef(false);

    const [isChange, setIsChange] = useState(false);
    const [displayVal, setDisplayVal] = useState<string>(format(initVal));
    const [numberVal, setNumberVal] = useState<number | null>(
      (returnZero ? typeof initVal === 'number' : initVal) ? Number(initVal) : null,
    );

    useEffect(() => {
      if (isChange) return;
      if (typeof value === 'number' || value === null) {
        if (value === null) {
          setDisplayVal('');
          setNumberVal(null);
          return;
        }
        /* Grouped when the field is being read, unformatted when it is being
        typed into. This effect also runs on mount, and with `autoFocus` the
        focus event has already fired by then (it comes from a layout effect,
        which is why `focusedRef` and not the `focused` state is what can be
        trusted here) — so formatting unconditionally would put the grouped
        string straight back under the caret that `handleFocus` had just
        cleared it from, and the first keystroke would be unparseable again
        (WA-3803).

        `format` rounds to `effectivePrecision` itself; the value must not be
        pre-mangled by string surgery first — that is how a `precision: 0` field
        used to display 1.9 as "19" instead of the rounded "2". */
        setDisplayVal(focusedRef.current ? formatRaw(value) : format(value));
        setNumberVal(returnZero ? Number(value) : Number(value) || null);
      }
    }, [isChange, value, format, formatRaw, returnZero, defaultValue]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsChange(true);
      /* Normalise the grouping the display may have left behind instead of
      rejecting the whole string because of it. Rejecting dropped the keystroke
      that arrived with it, silently — which is how digits went missing during
      fast order entry (WA-3803). Letters still fail the test below and are
      still refused, exactly as before. */
      let value = dropGroupSeparators(e.target.value);
      const isValid = /^-?(?!([.,]))[0-9]*[.,]?[0-9]*$/.test(value);
      if (isValid) {
        if (value) {
          const replacedToDot = value.replace(/,/g, '.');
          const numberValue = Number(replacedToDot);

          if (value === '-' || isNaN(numberValue)) {
            // when customer try write negative number. He start with '-' and without this code
            // he will have scary error. Something like "NaN (cast from the value `NaN`)"
            setDisplayVal(value);
            setNumberVal(null);
            onChange?.(null);
            return;
          }

          if (numberValue > max) {
            value = String(max);
          }
          if (numberValue < min) {
            value = String(min);
          }
        }
        setDisplayVal(value);
        let numVal;
        if (returnZero && !value) {
          numVal = null;
        } else numVal = Number(value.replace(/,/g, '.'));
        setNumberVal(numVal);
        onChange?.(numVal);
      }
    };

    const handleBlur = () => {
      focusedRef.current = false;
      setFocused(false);
      setIsChange(false);
      let res;
      if (returnZero && numberVal === null) res = null;
      else res = fixToPrecision(Number(numberVal));
      setDisplayVal(format(numberVal));
      onChange?.(res);
      onBlur?.(res);
    };

    const handleClick = (stepType: 'increase' | 'decrease'): void => {
      let newValue: number =
        stepType === 'increase' ? Number(numberVal) + step : Number(numberVal) - step;
      let canChange: boolean;
      if (stepType === 'increase') {
        if (!isNaN(min) && newValue < min) {
          newValue = min;
        }
        canChange = isNaN(max) ? true : newValue <= max;
      } else {
        if (!isNaN(max) && newValue > max) {
          newValue = max;
        }
        canChange = isNaN(min) ? true : newValue >= min;
      }
      if (canChange) {
        setDisplayVal(format(newValue));
        const numVal = fixToPrecision(newValue);
        setNumberVal(numVal);
        onChange?.(numVal);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      const code = e.code;
      if (keyChange) {
        if (code === 'ArrowUp') {
          e.preventDefault();
          handleClick('increase');
        }
        if (code === 'ArrowDown') {
          e.preventDefault();
          handleClick('decrease');
        }
      }
      onKeyDown?.(e);
    };

    const handleFocus = () => {
      /* Synchronous on purpose. This used to run in a `setTimeout(…, 0)`, so
      the grouped display string stayed in the box until that macrotask ran —
      and a macrotask is delayed exactly when the page is busy. Every digit
      typed in that window made the whole string fail `handleChange`'s validity
      test and was dropped without a trace (WA-3803). A focus event always
      precedes the first keystroke, so unformatting here closes the window
      instead of racing it. */
      focusedRef.current = true;
      setFocused(true);
      /* Only a formatted display needs unformatting. A string the user is
      already typing is raw by definition and must be left exactly as typed. */
      if (!isChange) setDisplayVal(formatRaw(numberVal));
    };

    const handleInputFocusByArrow = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.currentTarget as HTMLDivElement;
      if (target !== null) {
        const prevEl = target.previousElementSibling;
        const timer = setTimeout(() => {
          (prevEl as HTMLElement)?.focus();
          clearTimeout(timer);
        }, 0);
      }
    };

    return (
      <SBox
        controls={controls}
        sx={{
          width: '100%',
          maxWidth: (theme) =>
            width || (shrink ? theme.form.fieldShrinkWidth : theme.form.fieldWidth),
        }}
      >
        <TextField
          variant='outlined'
          size='small'
          fullWidth
          onChange={handleChange}
          onFocus={handleFocus}
          type='text'
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          ref={ref}
          {...props}
          focused={focused}
          value={displayVal}
          slotProps={{
            ...slotProps,
            input: {
              ...slotProps?.input,
              ...(controls && {
                endAdornment: (
                  <SBtnWrapper
                    direction='column'
                    justifyContent='flex-start'
                    alignItems='flex-start'
                    spacing={0}
                    width='fit-content'
                    onBlur={() => {
                      onBlur?.(numberVal);
                    }}
                    onMouseDown={(e) => {
                      handleInputFocusByArrow(e);
                      focused && e.preventDefault();
                    }}
                  >
                    <SIconButton
                      disabled={props.disabled}
                      tabIndex={-1}
                      onClick={() => handleClick('increase')}
                    >
                      <ExpandLessIcon />
                    </SIconButton>
                    <SIconButton
                      disabled={props.disabled || disableDecrease}
                      tabIndex={-1}
                      onClick={() => handleClick('decrease')}
                    >
                      <ExpandMoreIcon />
                    </SIconButton>
                  </SBtnWrapper>
                ),
              }),
            },

            htmlInput: {
              inputMode: 'numeric',
              ...slotProps?.htmlInput,
            },

            inputLabel: { shrink: true, ...slotProps?.inputLabel },
          }}
        />
      </SBox>
    );
  },
);

/**
 * Make a typed-in string parseable, without needing to know the locale.
 *
 * A number carries at most one decimal separator, so when a string holds more
 * than one, every separator before the last one has to be grouping — whichever
 * character the locale happens to use for it. Anything that is not a digit, a
 * sign or a separator (de-CH `’`, a space, a NBSP) is grouping too.
 *
 * Only reachable when a grouped value is still in the box as the user types;
 * `handleFocus` normally unformats the field before that can happen. Kept as a
 * second line of defence because the cost of getting it wrong is a keystroke
 * disappearing with no feedback (WA-3803).
 */
const dropGroupSeparators = (raw: string): string => {
  const cleaned = raw.replace(/[^\d.,-]/g, '');
  const lastSeparator = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
  if (lastSeparator < 0) return cleaned;
  return cleaned.slice(0, lastSeparator).replace(/[.,]/g, '') + cleaned.slice(lastSeparator);
};

const SIconButton = styled(IconButton)(() => ({
  padding: 0,
  width: 15,
  height: 15,
  '& svg': {
    fontSize: 17,
  },
}));

const SBtnWrapper = styled(Stack)(() => ({
  display: 'inline-flex',
}));

const SBox = styled(Box)<{ controls: boolean; shrink?: boolean }>(({ controls }) => ({
  position: 'relative',
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    appearance: 'none',
    margin: 0,
  },
  '& input': {
    appearance: 'textfield',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginRight: 0,
  },
  ...(controls && {
    '& input': {
      paddingRight: 23,
    },
  }),
}));

export type TNumericFieldProps = Omit<
  TextFieldProps,
  'onChange' | 'onBlur' | 'onKeyDown' | 'type'
> & {
  shrink?: boolean;
  defaultValue?: number | null;
  onChange?: (v: number | null) => void;
  onBlur?: (v: number | null) => void;
  onKeyDown?: (v: React.KeyboardEvent) => void;
  returnZero?: boolean;
  controls?: boolean;
  step?: number;
  min?: number;
  max?: number;
  precision?: number;
  noTrailingZeros?: boolean;
  width?: string;
  disableDecrease?: boolean;
  keyChange?: boolean;
  formattingToLocal?: boolean;
};
